<template>
    <div>
        <div class="paygrid-table" v-if="paygridData">
            <portal-target name="actionsPortal"></portal-target>

            <info-widget v-for="company in paygridData" :key="company.id" :collapsible="true" :is-expanded="true">
                <template v-slot:title><span class="text-muted">Company: </span> {{ company.name }}</template>
                <slot>
                    <PGFastTable
                        class="p-2"
                        :recordLabel="recordLabel"
                        :sections="company.sectionLabels"
                        :headers="headers"
                        :data="company.tableData"
                        :showSearch="true"
                        :showFilter="true"
                        :showMoreActions="false"
                        :bottomOffset="bottomOffset"
                        @click="onClick"
                        :isRowClickable="false"
                        @blur="onBlur"
                        actionsPortalTo="actionsPortal"
                        >
                        <template #section-status="{ source }">
                            <section-status-badges
                                v-if="source.sectionStatus"
                                :status="source.sectionStatus"
                                :obj-id="source.objId"
                                @reopen="onSectionReopen"
                            />
                        </template>
                    </PGFastTable>
                </slot>
            </info-widget>
            <!-- Every step shows, so a run can have no employees on this one -->
            <p v-if="!paygridData.length" class="text-muted pt-3">
                No {{ isSalariedMode ? 'salaried' : 'hourly' }} employees on this run.
            </p>

            <employee-sidebar
                :client="client"
                :employee-id="currentEmployee"
                v-if="currentEmployee"
                @close="currentEmployee = null"
                tab="payroll"
                @updated="$emit('refresh')"
                :key="currentEmployee"/>

        </div>
    </div>

</template>

<script>
import PGFastTable from '@/components/PGFastTable'
import TableFilter from '@/components/FastTable/Filter'
import EmployeeSidebar from '@/components/EmployeeSidebar'
import InfoWidget from '@/components/InfoWidget'
import SectionStatusBadges from './SectionStatusBadges'

export default {
    components: {PGFastTable, TableFilter, EmployeeSidebar, InfoWidget, SectionStatusBadges },
    props: {
        client: Object,
        payrollRun: Object,
        mode: String,
        // The client's CompanyEarningTypes, for sorting each position's earnings
        clientEarningTypes: {
            type: Array, default: () => [],
        },
        // Space the page reserves below the grid (the Back/Continue footer),
        // so the table sizes itself to the remaining height instead of the full viewport.
        bottomOffset: {
            type: Number, default: 0,
        },
    },
    emits: ['blur', 'click', 'open-earnings-statement', 'open-modify-earnings', 'add-separate-paycheck', 'section-reopen', 'refresh'],
    computed: {
        isSalariedMode() {
            return this.mode === 'salaried'
        },
        headers() {
            // The salaried step drops Rate of Pay and Overtime, and the hours column is
            // labeled Regular (salary earnings are always hours-based).
            if (this.isSalariedMode) {
                return [
                    {label: 'Employee', classes: 'cell-hide-subrow', isSearchable: true, type: "action"},
                    {label: 'Warnings', classes: 'cell-hide-subrow cw-warning', isSearchable: false, isFilterable: true},
                    {label: 'Position', classes: 'cw-9', isSearchable: true, isFilterable: true},
                    {label: 'Type', classes: 'cell-narrow', isSearchable: false, isFilterable: true},
                    {label: 'Regular', classes: 'cw-6', isSearchable: false, type: "decimal-input"},
                    {label: 'Amount', classes: 'cw-6', isSearchable: false, type: "currency-input"},
                ]
            }
            return [
                {label: 'Employee', classes: 'cell-hide-subrow', isSearchable: true, type: "action"},
                {label: 'Warnings', classes: 'cell-hide-subrow cw-warning', isSearchable: false, isFilterable: true},
                {label: 'Rate of Pay', classes: 'cw-8', isSearchable: false,},
                {label: 'Position', classes: 'cw-9', isSearchable: true, isFilterable: true},
                {label: 'Type', classes: 'cell-narrow', isSearchable: false, isFilterable: true},
                {label: 'Hours/Units', classes: 'cw-6', isSearchable: false, type: "decimal-input"},
                {label: 'Overtime', classes: 'cw-6', isSearchable: false, type: "decimal-input"},
                {label: 'Amount', classes: 'cw-6', isSearchable: false, type: "currency-input"},
            ]
        },
        paygridData() {
            return this.makePaygridData()
        },
        companyEarningTypeById() {
            const byId = {}
            this.clientEarningTypes.forEach(cet => {
                byId[cet.id] = cet
            })
            return byId
        },
        locationStatusesById() {
            const statusesById = {}
            if (this.payrollRun.location_statuses) {
                this.payrollRun.location_statuses.forEach(ls => {
                    statusesById[ls.id] = ls.status
                })
            }
            return statusesById
        }
    },
    data() {
        return {
            search: '',
            currentEmployee: null,
            recordLabel: "employees",
        }
    },
    methods: {
        makePaygridData() {
            const paygridData = []
            this.payrollRun.companies.forEach(company => {
                const companySectionLabels = []
                const companyTableData = []

                company.locations.forEach(location => {
                    const sectionData = []
                    let locationStatus = this.locationStatusesById[location.id] || null

                    location.employees.forEach((employee) => {

                        employee.paychecks.forEach((paycheck) => {
                            console.log('paycheck', paycheck)

                            let row = null

                            const positionsBySlug = {};
                            const positionFor = (positionSlug) => {
                                if (!positionsBySlug[positionSlug]) {
                                    positionsBySlug[positionSlug] = {
                                        slug: positionSlug,
                                        earnings: [],
                                        reimbursements: [],
                                    }
                                }
                                return positionsBySlug[positionSlug]
                            }

                            for (const earning of paycheck.earnings || []) {
                                // Each step shows only its own earnings: salaried jobs (pay
                                // types S / SNE) on the salaried step, everything else —
                                // including legacy earnings with no pay_type — on the hourly step.
                                if (this.isSalariedPayType(earning.pay_type) !== this.isSalariedMode) {
                                    continue
                                }
                                positionFor(earning.position_slug).earnings.push(earning);
                            }
                            // Reimbursements carry their job's pay type, so they show on the
                            // same step as that job's earnings. Owed ones (from elsewhere, claimed
                            // by this paycheck) show after them, read-only.
                            for (const reimbursement of [...(paycheck.reimbursements || []), ...(paycheck.owed_reimbursements || [])]) {
                                if (this.isSalariedPayType(reimbursement.pay_type) !== this.isSalariedMode) {
                                    continue
                                }
                                positionFor(reimbursement.position_slug).reimbursements.push(reimbursement);
                            }
                            const paycheckPositions = Object.values(positionsBySlug)

                            const employeeName = {
                                value: employee.name, //(employee.last_name + ", " + employee.first_name).toUpperCase(),
                                html: `<a class="uppercase">${employee.name}</a>`,
                                callback: (val) => {
                                    this.openSidebar(val)
                                },
                                callbackValue: employee.id
                            }
                            if (paycheck.is_separate_check) {
                                employeeName.html += ` <i class="ml-1 far fa-fw fa-money-check" style="color: #206067;"></i>`
                            }

                            // The paycheck's first line becomes the row (with the actions menu);
                            // every later line is a subrow under it.
                            const addLine = (cells, object, idx) => {
                                if (!row) {
                                    const employeeId = employee.id
                                    const earningsStatementId = paycheck.earnings_statement_id
                                    row = {
                                        id: idx,
                                        object: object,
                                        isActive: false,
                                        disableHover: true,
                                        cells: cells,
                                        companyId: company.id,
                                        locationId: location.id,
                                        employeeId: employee.id,
                                        paycheckId: paycheck.id,
                                        // positionName: position.name,
                                        // earningType: earning.earning_type,
                                        subrows: [],
                                        actionsMenuItems: [
                                            {icon: 'far fa-fw fa-pen mr-1', label: 'Modify Earnings', callback: () => this.$emit('open-modify-earnings', [employeeId, earningsStatementId, 'earnings'])},
                                            {icon: 'far fa-fw fa-percent mr-1', label: 'Modify Taxes & Deductions', callback: () => this.$emit('open-modify-earnings', [employeeId, earningsStatementId, 'taxes-deductions'])},
                                            {icon: 'far fa-fw fa-plus mr-1', label: 'Separate Check', callback: () => this.$emit('add-separate-paycheck', [employeeId, false])},
                                            {icon: 'fas fa-fw fa-minus-circle mr-1', label: 'Void/Correct a Previous Check'},
                                            {icon: 'far fa-fw fa-eye mr-1', label: 'Preview Earnings Statement', callback: () => this.$emit('open-earnings-statement', [employeeId, earningsStatementId])},
                                        ],
                                    }
                                } else {
                                    row.subrows.push(cells)
                                }
                            }

                            // Sort each position's earnings by sort priority, then name, with ones
                            // added by hand in Modify Earnings last, in the order added. The sort is
                            // stable, so ties keep their order, such as a piecemeal job's two rows.
                            paycheckPositions.forEach((position) => {
                                position.earnings.sort((a, b) => {
                                    if (Boolean(a.is_custom) !== Boolean(b.is_custom)) {
                                        return a.is_custom ? 1 : -1
                                    }
                                    if (a.is_custom) {
                                        return 0
                                    }
                                    return (this.earningSortPriority(a) - this.earningSortPriority(b))
                                        || (a.earning_type_name || '').localeCompare(b.earning_type_name || '')
                                })
                            })

                            // paycheck.positions.forEach((position, positionIdx) => {
                            paycheckPositions.forEach((position, positionIdx) => {

                                position.earnings.forEach((earning, earningIdx) => {
                                    earning.employee_id = employee.id

                                    let payRateStr = this.$filters.dollars(earning.rate)
                                    // const payRate = {
                                    //     value: earning.rate,
                                    //     _meta: { field: 'rate' }
                                    // }
                                    // A position default with no rate from the job or
                                    // CompanyEarningType: the rate is entered here, for this run
                                    // only (PaygroupRun._build_earnings_for_job)
                                    const payRateInput = earning.is_rate_editable ? {
                                        value: earning.rate,
                                        _meta: { type: 'currency-input', field: 'rate', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.rate) }
                                    } : null
                                    const earningHoursOrUnits = {
                                        value: '',
                                        _meta: {}
                                    }
                                    // Each editable cell carries direct refs to the live `earning`
                                    // and `paycheck` it edits, so the commit handler mutates the model
                                    // in place — no id/slug key to reconstruct or re-look-up.
                                    // A zero shows as 0.00 only where someone entered it. Hours, units
                                    // and overtime are stored as typed, so any non-blank value was
                                    // entered; the amount is recalculated on every save, so an entered
                                    // one is flagged with is_amount_entered.
                                    const earningOvertimeHours = {
                                        value: earning.overtime_hours,
                                        _meta: { field: 'overtime_hours', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.overtime_hours) }
                                    }
                                    const earningAmount = {
                                        value: earning.amount,
                                        _meta: { field: 'amount', earning: earning, paycheck: paycheck, showZero: Boolean(earning.is_amount_entered) }
                                    }
                                    // Earnings added by hand in Modify Earnings or Separate Check
                                    // get an orange-bordered Amount input
                                    if (earning.is_custom) {
                                        earningAmount._meta.additionalCSSClasses = ['cell-custom-earning']
                                    }
                                    if (earning.earning_type_type == 'hourly') {
                                        payRateStr += " /hr"
                                        if (payRateInput) {
                                            payRateInput._meta.suffix = '/hr'
                                        }
                                        earningHoursOrUnits.value = earning.hours
                                        earningHoursOrUnits._meta = { field: 'hours', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.hours) }
                                        earningAmount._meta.disabled = true
                                    } else if (earning.earning_type_type == 'per-unit') {
                                        payRateStr += " /unit"
                                        if (payRateInput) {
                                            payRateInput._meta.suffix = '/unit'
                                        }
                                        earningHoursOrUnits.value = earning.units
                                        earningHoursOrUnits._meta = { field: 'units', earning: earning, paycheck: paycheck, showZero: this.hasEnteredValue(earning.units) }
                                        earningOvertimeHours._meta.disabled = true
                                        earningAmount._meta.disabled = true
                                    } else if (earning.earning_type_type == 'amount') {
                                        earningHoursOrUnits._meta.disabled = true
                                        earningOvertimeHours._meta.disabled = true
                                    }

                                    let cells
                                    if (this.isSalariedMode) {
                                        cells = [
                                            employeeName,
                                            employee.warning_count,
                                            position.slug,
                                            earning.earning_type_name,
                                            earningHoursOrUnits,
                                            earningAmount,
                                        ]
                                    } else {
                                        cells = [
                                            employeeName,
                                            employee.warning_count,
                                            // payRate,
                                            payRateInput || payRateStr,
                                            position.slug,
                                            earning.earning_type_name,
                                            earningHoursOrUnits,
                                            earningOvertimeHours,
                                            earningAmount,
                                        ]
                                    }

                                    addLine(cells, earning, earningIdx)
                                })

                                // Reimbursements are an amount only; hours and overtime don't apply.
                                position.reimbursements.forEach((reimbursement, reimbursementIdx) => {
                                    const reimbursementType = reimbursement.name
                                    const notApplicable = {value: '', _meta: {disabled: true}}
                                    const reimbursementAmount = {
                                        value: reimbursement.amount,
                                        _meta: { field: 'amount', reimbursement: reimbursement, paycheck: paycheck, disabled: Boolean(reimbursement.employee_reimbursement_id) }
                                    }

                                    let cells
                                    if (this.isSalariedMode) {
                                        cells = [
                                            employeeName,
                                            employee.warning_count,
                                            position.slug,
                                            reimbursementType,
                                            notApplicable,
                                            reimbursementAmount,
                                        ]
                                    } else {
                                        cells = [
                                            employeeName,
                                            employee.warning_count,
                                            '',
                                            position.slug,
                                            reimbursementType,
                                            notApplicable,
                                            notApplicable,
                                            reimbursementAmount,
                                        ]
                                    }

                                    addLine(cells, reimbursement, position.earnings.length + reimbursementIdx)
                                })
                            })
                            // All of the paycheck's earnings can belong to the other step,
                            // in which case there is no row for this one.
                            if (row) {
                                sectionData.push(row)
                            }
                        })
                    })
                    // Hide locations whose employees all belong to the other step.
                    if (sectionData.length === 0) {
                        return
                    }
                    companySectionLabels.push(
                        {title: location.name, defaultIsClosed: false, id: location.id, objId: location.id, hasDateFilter: false, status: locationStatus, inputsDisabled: ['ready-for-approval', 'approved', 'completed'].includes(locationStatus)},
                    )
                    companyTableData.push(sectionData)
                })
                // Likewise hide companies with no locations left to show.
                if (companySectionLabels.length === 0) {
                    return
                }
                paygridData.unshift({
                    id: company.id,
                    name: company.name,
                    sectionLabels: companySectionLabels,
                    tableData: companyTableData
                })
            })
            return paygridData
        },
        isSalariedPayType(payType) {
            return payType === 'S' || payType === 'SNE'
        },
        hasEnteredValue(value) {
            return value !== '' && value != null
        },
        earningSortPriority(earning) {
            // The company type's sort priority (a copy of the global type's unless customized);
            // unknown types go last
            const cet = this.companyEarningTypeById[earning.company_earning_type_id]
            return cet ? cet.sort_priority : Number.MAX_SAFE_INTEGER
        },
        onClick($event) {
            if ($event.target == 'employee-name') {
                this.currentEmployee = $event.employee_id
                this.$emit('click', $event)
            }
        },
        onBlur(msg) {
            this.$emit('blur', msg)
        },
        onSectionReopen(locationId) {
            // Find the location object so the parent can display its name
            for (const company of this.payrollRun.companies) {
                const location = (company.locations || []).find(l => l.id === locationId)
                if (location) {
                    this.$emit('section-reopen', location)
                    return
                }
            }
            // fallback: emit with just the id if location not found
            this.$emit('section-reopen', { id: locationId, name: '' })
        },
        openSidebar(employeeId) {
            this.currentEmployee = employeeId
        },
    },
}
</script>
